// Las skills de FÁBRICA: markdown en `fabrica/`, importado crudo por Vite y parseado
// una vez al cargar el módulo — ADR-2026-09-09-el-agente-tiene-skills.
//
// Agregar una skill = escribir el .md con su frontmatter (name / description /
// when_to_use) y sumarlo acá. El test de `skillsDeFabrica.test.ts` verifica que cada
// una parsee, que los nombres no se repitan, que las herramientas que nombran existan
// y que ninguna sea tan larga que pese de más al cargarla.

import { ensamblarSkill, parsearSkill, type SkillDelAgente } from './skill';

// Los NÚCLEOS de las skills de imagen: el conocimiento puro del prompt, que comparten el
// agente y los escritores de Crear y de Editar del backend (copia generada en
// `atlas-backend/src/services/ads/escritorDeImagen/nucleos.generated.ts`, vigilada por
// `nucleos.espejo.test.ts`) — ADR-2026-10-06-los-nucleos-de-imagen-se-comparten.
import nucleoFotoDeProducto from './fabrica/nucleos/foto-de-producto.nucleo.md?raw';
import nucleoFotoConModelo from './fabrica/nucleos/foto-con-modelo.nucleo.md?raw';
import nucleoEditarProducto from './fabrica/nucleos/editar-producto-al-detalle.nucleo.md?raw';
import nucleoSegunElRubro from './fabrica/nucleos/segun-el-rubro.nucleo.md?raw';
import nucleoEstilo from './fabrica/nucleos/estilo-y-direccion-de-arte.nucleo.md?raw';

export const NUCLEOS_DE_IMAGEN = {
  'foto-de-producto': nucleoFotoDeProducto,
  'foto-con-modelo': nucleoFotoConModelo,
  'editar-producto-al-detalle': nucleoEditarProducto,
  'segun-el-rubro': nucleoSegunElRubro,
  'estilo-y-direccion-de-arte': nucleoEstilo,
} as const;
export type NombreDeNucleo = keyof typeof NUCLEOS_DE_IMAGEN;

import anuncioGrafico from './fabrica/anuncio-grafico.md?raw';
import ayudarEnUnModo from './fabrica/ayudar-en-un-modo.md?raw';
import brainstorming from './fabrica/brainstorming.md?raw';
import contexto from './fabrica/contexto.md?raw';
import copiarUnVideo from './fabrica/copiar-un-video.md?raw';
import crearSkill from './fabrica/crear-skill.md?raw';
import disenoDeIndumentaria from './fabrica/diseno-de-indumentaria.md?raw';
import editarProductoAlDetalle from './fabrica/editar-producto-al-detalle.md?raw';
import segunElRubro from './fabrica/segun-el-rubro.md?raw';
import estiloYDireccionDeArte from './fabrica/estilo-y-direccion-de-arte.md?raw';
import estudioDeVideo from './fabrica/estudio-de-video.md?raw';
import flujos from './fabrica/flujos.md?raw';
import flujosCampana from './fabrica/flujos-campana.md?raw';
import fotoConModelo from './fabrica/foto-con-modelo.md?raw';
import fotoDeProducto from './fabrica/foto-de-producto.md?raw';
import investigarCompetencia from './fabrica/investigar-competencia.md?raw';
import lanzamientos from './fabrica/lanzamientos.md?raw';
import tiendanube from './fabrica/tiendanube.md?raw';
import instagram from './fabrica/instagram.md?raw';
import anuncios from './fabrica/anuncios.md?raw';
import mercadolibre from './fabrica/mercadolibre.md?raw';
import memoria from './fabrica/memoria.md?raw';
import modosDeImagen from './fabrica/modos-de-imagen.md?raw';
import modosDeVideo from './fabrica/modos-de-video.md?raw';
import moodboard from './fabrica/moodboard.md?raw';
import pdf from './fabrica/pdf.md?raw';
import planificar from './fabrica/planificar.md?raw';
import planillas from './fabrica/planillas.md?raw';
import sesionDeFotos from './fabrica/sesion-de-fotos.md?raw';
import video from './fabrica/video.md?raw';
import videoAnuncio from './fabrica/video-anuncio.md?raw';
import videoUgc from './fabrica/video-ugc.md?raw';

/** En el orden en que se listan en el prompt: primero las de imagen (lo más usado),
 *  después las de método, después las de dominio, y al final las de la APP (ayudar
 *  adentro de un modo, qué hace cada modo — ADR-2026-09-10-el-agente-toca-la-pantalla —
 *  y dirigir un video en el editor —
 *  ADR-2026-09-11-el-director-del-estudio-se-funde-en-el-agente). */
const FUENTES: string[] = [
  // Las tres de imagen que se parten en núcleo + sección del agente: el agente carga el
  // texto ensamblado, como siempre fue un solo archivo.
  ensamblarSkill(fotoConModelo, NUCLEOS_DE_IMAGEN['foto-con-modelo']),
  ensamblarSkill(fotoDeProducto, NUCLEOS_DE_IMAGEN['foto-de-producto']),
  sesionDeFotos,
  ensamblarSkill(editarProductoAlDetalle, NUCLEOS_DE_IMAGEN['editar-producto-al-detalle']),
  // Lo que cambia por rubro y cómo se escribe un estilo que no es foto realista: cada una es
  // una skill porque su cuerpo es un núcleo compartido con los escritores del backend
  // (ADR-2026-10-06-los-nucleos-por-rubro-y-estilo).
  ensamblarSkill(segunElRubro, NUCLEOS_DE_IMAGEN['segun-el-rubro']),
  ensamblarSkill(estiloYDireccionDeArte, NUCLEOS_DE_IMAGEN['estilo-y-direccion-de-arte']),
  moodboard,
  anuncioGrafico,
  video,
  // Los tipos del Creador de video como skills del agente: el agente es el redactor
  // (ADR-2026-09-22-el-agente-es-el-redactor-de-video-con-los-tipos-como-skills).
  videoUgc,
  videoAnuncio,
  copiarUnVideo,
  planificar,
  brainstorming,
  contexto,
  memoria,
  crearSkill,
  investigarCompetencia,
  planillas,
  pdf,
  lanzamientos,
  // La tienda del comerciante (ADR-2026-09-18-tiendanube-en-el-chat).
  tiendanube,
  // El Instagram de la marca por la API oficial (ADR-2026-09-24-instagram-en-el-chat).
  instagram,
  // Los anuncios de Meta de la marca, por el MCP oficial (ADR-2026-09-24-anuncios-de-meta-en-el-chat).
  anuncios,
  // El Mercado Libre de la marca (ADR-2026-09-28-mercadolibre-en-el-chat).
  mercadolibre,
  ayudarEnUnModo,
  modosDeImagen,
  modosDeVideo,
  estudioDeVideo,
  disenoDeIndumentaria,
  // El canvas de Flujos dentro del agente (ADR-2026-09-15-flujos-director-en-el-agente).
  flujos,
  flujosCampana,
];

export const SKILLS_DE_FABRICA: SkillDelAgente[] = FUENTES.map((md) => parsearSkill(md, 'fabrica'));

export const NOMBRES_DE_FABRICA: string[] = SKILLS_DE_FABRICA.map((s) => s.nombre);
